/**
 * The add/remove links the cocoon gem renders for nested form rows: 3D tours
 * and videos on the venue profile, videos on the supplier profile, menus on
 * the catering admin, and the highlights on both landing page forms.
 *
 * `link_to_add_association` carries one row's markup in a
 * `data-association-insertion-template` attribute, with the field index left
 * as the placeholder `new_<association>`. Clicking the link copies that markup
 * into the page with a fresh index substituted in, and Rails builds one nested
 * record per distinct index on submit — so two rows sharing an index collapse
 * into one record.
 *
 * The index appears in two shapes and both have to be rewritten: `[new_videos]`
 * inside `name`, and `_new_videos_` inside `id` and every `label[for]` pointing
 * at it. Rewriting only the first leaves each added row carrying the same
 * element id as its siblings, with labels focusing the wrong input.
 *
 * The placeholder is spelled with the **plural** association name, because
 * `render_association` renders the template with `child_index:
 * "new_#{association}"` — while `data-association` on the link is the
 * singular. The singular is kept as a fallback for a link whose template was
 * built the other way round; only one of the two can match a given template.
 *
 * The gem supplies that markup and nothing else here; its own client script is
 * not loaded.
 */

import Honeybadger from '@honeybadger-io/js';
import escapeRegExp from 'lodash/escapeRegExp';
import { delegate } from 'common/delegate';
import { REMOVE_LINK_SELECTOR } from 'common/nestedFields';

const ADD_SELECTOR = '.add_fields';
const DESTROYED_SELECTOR = '.remove_fields.existing.destroyed';
const DEFAULT_WRAPPER_CLASS = 'nested-fields';

let initialized = false;
let rowsAdded = 0;

export default function initializeNestedFields(): void {
  // The sweep is idempotent, so it runs on every call; the delegates must bind
  // exactly once or a single click would be handled twice.
  hideDestroyedRows();

  if (initialized) return;
  initialized = true;

  delegate(document, 'click', ADD_SELECTOR, handleAddClick);
  delegate(document, 'click', REMOVE_LINK_SELECTOR, handleRemoveClick);
}

function handleAddClick(event: MouseEvent, link: Element): void {
  if (!(link instanceof HTMLElement)) return;

  // The link is an `<a href="#">`, so the default would land a bare `#` in the
  // address bar on every added row.
  event.preventDefault();

  const template = link.dataset.associationInsertionTemplate;
  const insertionNode = link.parentElement;
  if (!template || !insertionNode) return;

  const index = nextIndex();
  const html =
    substituteIndex(template, link.dataset.associations, index) ??
    substituteIndex(template, link.dataset.association, index);

  if (html === null) {
    // Inserting the template unchanged would give the row the literal
    // `new_<association>` index, which Rails folds into a single record along
    // with every other row carrying it. Nothing is added instead — and since
    // that leaves no trace on the page, the report is the only way anyone
    // learns the link is dead.
    Honeybadger.notify(
      new Error('Nested form row has no index placeholder to substitute'),
      {
        context: {
          association: link.dataset.association,
          associations: link.dataset.associations
        }
      }
    );
    return;
  }

  // The row goes before the element holding the link, which keeps the rows
  // above the control that adds them.
  insertionNode.before(...parseRow(html));
}

function handleRemoveClick(event: MouseEvent, link: Element): void {
  if (!(link instanceof HTMLElement)) return;

  event.preventDefault();

  const row = rowFor(link);

  if (link.classList.contains('dynamic')) {
    // Nothing was ever persisted for this row, so there is nothing to tell the
    // server about.
    row?.remove();
    return;
  }

  // A saved row has to reach the server for Rails to destroy it, so it stays
  // in the DOM with `_destroy` set and is only hidden. The gem emits that
  // hidden input immediately before the link, which is what makes the sibling
  // lookup below find it.
  //
  // The flag is set whether or not the wrapper turned up: a `wrapper_class:`
  // that matches no ancestor costs the row its hiding, but a click that goes
  // through and saves the record anyway is the worse of the two failures.
  const destroyField = link.previousElementSibling;
  if (
    destroyField instanceof HTMLInputElement &&
    destroyField.type === 'hidden'
  ) {
    destroyField.value = '1';
  }

  if (row) hide(row);
}

/**
 * Rows a failed save came back with already marked for destruction. Rails
 * re-renders them so the form round-trips, but they are on their way out and
 * should not be presented as editable.
 */
function hideDestroyedRows(): void {
  document.querySelectorAll(DESTROYED_SELECTOR).forEach(link => {
    if (!(link instanceof HTMLElement)) return;

    const row = rowFor(link);
    if (row) hide(row);
  });
}

/**
 * Rails builds one nested record per distinct index, so every added row needs
 * an index of its own. The counter separates rows added inside the same
 * millisecond, which two quick clicks on the add link produce.
 */
function nextIndex(): number {
  const index = Date.now() + rowsAdded;
  rowsAdded += 1;
  return index;
}

/**
 * Replaces the `new_<association>` index placeholder throughout one row of
 * markup. Returns null when that association name does not appear in it, which
 * is how the caller tells the two spellings apart.
 *
 * Every occurrence is replaced. A placeholder left behind is worse than no row
 * at all: Rails folds each field still carrying `new_<association>` into one
 * shared record, so the row saves part of its values against a record it does
 * not own.
 */
function substituteIndex(
  html: string,
  association: string | undefined,
  index: number
): string | null {
  if (!association) return null;

  const name = escapeRegExp(association);

  const braced = new RegExp(`\\[new_${name}\\]`, 'g');
  const named = html.replace(braced, `[${index}]`);
  if (named === html) return null;

  const underscored = new RegExp(`_new_${name}_(\\w*)`, 'g');
  return named.replace(underscored, `_${index}_$1`);
}

function parseRow(html: string): ChildNode[] {
  // `<template>` parses content-restricted elements such as `<tr>`, which
  // `innerHTML` on a plain container would drop on the floor.
  const holder = document.createElement('template');
  holder.innerHTML = html;

  // The live child list empties as the nodes are moved into the document.
  return Array.from(holder.content.childNodes);
}

function rowFor(link: HTMLElement): HTMLElement | null {
  // `wrapper_class:` is per call site, so the row is whatever that view told
  // the gem to wrap it in.
  const wrapperClass = link.dataset.wrapperClass || DEFAULT_WRAPPER_CLASS;

  const row = link.closest(`.${wrapperClass}`);
  return row instanceof HTMLElement ? row : null;
}

function hide(element: HTMLElement): void {
  element.style.display = 'none';
}
